import React, { useEffect, useState } from "react";
import { CanvasesRefType, CardContentType, CardDatumType, CardType, emptyCardContent, LanguageEnum, noLanguageFields } from "../types";
import "./CardEditor.css";
import CardErrorSvg from "../assets/svgs/CardErrorSvg";
import { translateText } from "../assets/translations";
import { useAppDispatch } from "../redux/hooks";
import { cardApi } from "../api/card";
import { fetchPreload } from "../redux/slices/preloadSlice";
import { useUser } from "../hooks/useUser";
import { usePreload } from "../hooks/usePreload";
import { useNavigate } from "react-router-dom";
import MockCard from "../assets/components/MockCard";
import "./CardViewer.css";
import iconPhone from '../assets/svgs/IconPhone.svg';
import iconLink from '../assets/svgs/IconLink.svg';
import iconEmail from '../assets/svgs/IconEmail.svg';
import iconAddress from '../assets/svgs/IconAddress.svg';
import { createPortal } from "react-dom";
import LanguageToggle from "./LanguageToggle";
import { notify } from "../utils/notice";

const PREVENT_SAVING = false;

const FIELD_DISPLAY_ORDER: (keyof CardContentType)[] = [
  'lastName',
  'firstName', 
  'lastNamePhonetic',
  'firstNamePhonetic',
  'companyName',
  'branch',
  'title',
  'email',
  'url',
  'headPhone',
  'headFax', 
  'headAddress',
  'phone',
  'mobile',
  'fax',
  'address',
  'address2',
  'address3',
  'address4',
  'address5',
];

/**
 * Props for the CardEditor component.
 * @typedef {Object} CardDataEditorProps
 * @property {{ front: CardContentType | null, back: CardContentType | null, merged: CardContentType | null } | null} scannedBusinessCard
 * - Contains raw data from a scanned business card. Includes front, back, and merged data.
 * - Expected to be `null` if not provided.
 * @property {number | null} cardKey
 * - Represents the ID of an existing business card the user wants to edit.
 * - Expected to be `null` if not provided.
 */
type CardDataEditorProps = {
  canvases: CanvasesRefType;
  unsavedCanvas: { front: boolean, back: boolean };
  setUnsavedCanvas: React.Dispatch<React.SetStateAction<{ front: boolean; back: boolean; }>>;
  scannedBusinessCard: { front: CardContentType | null, back: CardContentType | null, merged: CardContentType | null } | null;
  isCaptureMode: boolean;
  setIsCaptureMode: (isCaptureMode: boolean) => void;
  isOwner: boolean;
  cardKey: number | null;
  isUploadOnly: boolean;
  isStartEditMode: boolean;
};

type DifferenceEntry = {
  field: keyof CardContentType;
  content: CardDatumType[] | null;
};

type DifferenceResultType = {
  existing: DifferenceEntry[];
  active: DifferenceEntry[];
};

type DuplicateMessageDataType = {
  message: string;
  existingCard: CardType;
  options: "none" | "replace" | "add";
}

/**
 * CardEditor Component
 *
 * This component allows users to review and edit business card data.
 * It handles raw data from scanned business cards (`scannedBusinessCard`)
 * or edits data for an existing business card (`cardKey`).
 *
 * Behavior:
 * - If no inputs are provided, displays a "No Data" message.
 * - If both inputs are provided, it combines the existing data (`cardKey`) with new data (`scannedBusinessCard`).
 * - For any data given, allows the user to check, correct, and modify any content.
 *
 * @component
 * @param {CardDataEditorProps} props - Component props.
 * @returns {JSX.Element} A UI for editing or verifying business card data.
 */
const CardViewer: React.FC<CardDataEditorProps> = ({ canvases, unsavedCanvas, scannedBusinessCard, isCaptureMode, setIsCaptureMode, isOwner, cardKey, isUploadOnly, isStartEditMode }) => {
  const dispatch = useAppDispatch();

  const user = useUser();
  const { userKey, userToken } = user;

  const preload = usePreload();
  const preloadUsers = preload?.users;
  const preloadUser = preloadUsers?.find((u) => u.externalUserKey === userKey);
  const ownerKey = preloadUser?.userKey;
  const isAdmin = preloadUser?.isAdmin;

  const [isEditing, setIsEditing] = useState<boolean>(isStartEditMode === true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [duplicateMessageData, setDuplicateMessageData] = useState<DuplicateMessageDataType | null>(null);

  const [language, setLanguage] = useState<LanguageEnum>("japanese");

  const storedCards = preload.cards;
  const storedCard = storedCards?.find(card => card.cardKey === cardKey) || null;

  const [scannedCardState, setScannedCardState] = useState<{ front: CardContentType | null, back: CardContentType | null, merged: CardContentType | null } | null>(scannedBusinessCard);
  const [imageLoadError, setImageLoadError] = useState<{ front: boolean, back: boolean }>({ front: false, back: false });

  const [activeCardContent, setActiveCardContent] = useState<CardContentType | null>(null);
  const [activeTags, setActiveTags] = useState<number[]>(storedCard?.tags || []);
  const [selectedOptions, setSelectedOptions] = useState<Partial<Record<keyof CardContentType, { language: LanguageEnum, optionIndex: number }[]>>>({});
  const [selectedTags, setSelectedTags] = useState<Record<number, number | null>>({});
  const [isPublic, setIsPublic] = useState<boolean>(true);

  const allTags = preload?.tags || [];
  const storedTags = storedCard?.tags || [];
  const isTagsChanged = storedTags.length !== activeTags.length || storedTags.some(tag => !activeTags.includes(tag)) || activeTags.some(tag => !storedTags.includes(tag));

  const navigate = useNavigate();

  useEffect(() => {
    // Usually, if there is already active card data, don't import scanned card data or existing card data into it
    // So, only merge in external data (scanned or existing in database) if the active data is null
    // This prevents the user's edits from being overwritten by previous data
    let doMerge = activeCardContent === null;

    // However, if there's a newly scanned card, we should import it into the active card data
    if (JSON.stringify(scannedCardState) !== JSON.stringify(scannedBusinessCard)) {
      setScannedCardState(scannedBusinessCard);
      doMerge = true;
    }

    if (!doMerge) return;

    // There are 3 sources of content: existing data (from the server), scanned data, and active data (being edited)
    // Active data is what the user has edited, and is the most important
    // Scanned data is the most recent data from the scanner, and is more current than data from the server
    // Server data is the oldest data, so it's used if there is no active data or scanned data
    // If the user is the owner of the card, we need to remove data from server data from the face of any scanned cards
    // Order of events:
    // Start with server data (stored card)
    // Then if this is the owner of the card, determine if there is scanned data, and if so, for which face, then delete that face from the stored card (Backlog task 97)
    // Then merge in the scanned data
    // Then merge in the active data
    let mergedContent = null;
    if (storedCard) { mergedContent = storedCard.content; } // server data
    if (isOwner) { // it's this user's card
      const hasScannedFront = scannedBusinessCard?.front !== null && scannedBusinessCard?.front !== undefined;
      const hasScannedBack = scannedBusinessCard?.back !== null && scannedBusinessCard?.back !== undefined;
      if (hasScannedFront) {
        mergedContent = removeFaceContent(mergedContent, "front"); // User has scanned new front data, so remove the front data from the server data
      }
      if (hasScannedBack) {
        mergedContent = removeFaceContent(mergedContent, "back"); // User has scanned new back data, so remove the back data from the server data
      }
    }
    if (scannedBusinessCard) { mergedContent = mergeCardContents(mergedContent, scannedBusinessCard.merged); } // add in scanned data
    if (activeCardContent) { mergedContent = mergeCardContents(mergedContent, activeCardContent); } // add in active data
    setActiveCardContent(mergedContent);
  }, [scannedBusinessCard, storedCard]);


  // Tags are easier than other data, because it is never captured from a scan, so it's always just what's saved, overwritten by the user's input
  useEffect(() => {
    setActiveTags(storedCard?.tags || []);
    const storedSelectedTags = storedCard?.tags.reduce<Record<number, number | null>>((tags, tag) => {
      const currentTag = allTags.find(t => t.tagKey === tag);
      const parentTag = allTags.find(t => t.tagKey === currentTag?.parentKey);
      if (parentTag !== undefined && parentTag.tagKey !== undefined) {
        tags[parentTag.tagKey] = currentTag?.tagKey || null;
      }
      return tags;
    }, {}) || {};
    console.log("Stored selected tags:");
    console.log(storedSelectedTags);
    setSelectedTags(storedSelectedTags);

    // Is there a stored card? If no, default to public (it's a new card)
    // If there is a stored card, set to non-public only if the card was previously explicitly set to non-public
    const isPublic = storedCard ? (storedCard.isPublic === false ? false : true) : true;

    setIsPublic(isPublic);
  }, [storedCard]);

  const tagGroups = allTags.reduce<Record<number, { title: string, english: string | undefined, japanese: string | undefined, options: { tagKey: number; name: string, english: string | undefined, japanese: string | undefined }[] }>>((groups, tag) => {
    if (tag.parentKey === null) {
      groups[tag.tagKey] = { title: tag.name, english: tag.english, japanese: tag.japanese, options: [] };
    } else if (tag.parentKey in groups) {
      groups[tag.parentKey].options.push({ tagKey: tag.tagKey, name: tag.name, english: tag.english, japanese: tag.japanese });
    }
    return groups;
  }, {});

  //const isEditMade = JSON.stringify(activeCardData) !== JSON.stringify(mergeCardContents(scannedCardState?.merged || null, storedCard?.content || null));

  function handleTagToggle(tagKey: number) {
    setActiveTags((prev) => {
      const newTags = prev.includes(tagKey) ? prev.filter(tag => tag !== tagKey) : [...prev, tagKey];
      return newTags;
    });
  }

  function handleTagChange(parentKey: number, selectedTagKey: number | null) {
    setSelectedTags((prev) => ({ ...prev, [parentKey]: selectedTagKey }));
  }

  function HandleChangeField(event: React.ChangeEvent<HTMLInputElement>, field: keyof CardContentType) {
    const incomingText = event.target.value;
    //console.log("Change field: " + field + " to " + incomingText + " in " + language);
    setActiveCardContent((prevData) => {
      if (!prevData) { prevData = { ...emptyCardContent }; }
      const newData = { ...prevData };

      // The field being changed has no language (e.g. phone number, email, etc.)
      const isNoLanguageField = noLanguageFields[field];

      // Remove the existing data, in the selected language, if it exists
      // If there's no language needed for this field, remove the null language entry
      const currentField = newData[field]?.filter?.(item => item.language !== (isNoLanguageField ? null : language)) || [];
      if (incomingText !== "") { currentField.push({ content: incomingText, language: (isNoLanguageField ? null : language) }) }

      newData[field] = currentField;
      return newData;
    });
  }

  function handleAddField() {
    const selectElement = document.getElementById("addFieldSelect") as HTMLSelectElement;
    if (!selectElement) { return; }
    const selectedField = selectElement.value as keyof CardContentType;
    setActiveCardContent((prevData) => {
      if (!prevData) { prevData = { ...emptyCardContent }; }
      const newData = { ...prevData };
      if (noLanguageFields[selectedField]) {
        // The field being added has no language (e.g. phone number, email, etc.)
        // Delete existing "no language" content, if any (should never exist, but make sure anyway)
        const currentField = newData[selectedField]?.filter?.(item => item.language === null) || [];
        currentField.push({ content: "", language: null });
        newData[selectedField] = currentField;
      } else {
        // The field being added has a language (e.g. name, address, etc.)
        // Delete existing content in the selected language, if any
        const currentField = newData[selectedField]?.filter?.(item => item.language !== language) || [];
        currentField.push({ content: "", language: language });
        newData[selectedField] = currentField;
      }
      return newData;
    });
  }

  function handleDeleteField(fieldToDelete: keyof CardContentType) {
    setActiveCardContent((prevData) => {
      if (!prevData) { prevData = { ...emptyCardContent }; }
      const newData = { ...prevData };

      // This block would delete only the content in the selected language, but that doesn't feel like what a delete button should do
      //if (noLanguageFields[fieldToDelete]) {
      //    // The field being added has no language (e.g. phone number, email, etc.)
      //    // Delete existing "no language" content by looking for language is null
      //    newData[fieldToDelete] = newData[fieldToDelete]?.filter?.(item => item.language !== null) || [];
      //} else {
      //    // The field being added has a language (e.g. name, address, etc.)
      //    // Delete existing content in the selected language by looking for language is not the selected language
      //    newData[fieldToDelete] = newData[fieldToDelete]?.filter?.(item => item.language !== language) || [];
      //}
      //if (newData[fieldToDelete]?.length === 0) {
      //    newData[fieldToDelete] = null;
      //}

      newData[fieldToDelete] = null;
      const allFieldsAreNull = Object.values(newData).every(value => value === null);

      return allFieldsAreNull ? null : newData;
    });
  }

  async function handleSaveCard() {
    if (!userKey || !userToken) {
      notify("ログイン情報が確認できないため、名刺を保存できません。もう一度ログインしてください。", "エラー");
      console.log("No user key or token, cannot save card");
      return;
    }
    if (!activeCardContent) {
      notify("保存する名刺の内容がありません。");
      console.log("No active card content to save");
      return;
    }
    if (isSaving && !PREVENT_SAVING) {
      notify("保存中です。しばらくお待ちください。");
      console.log("Already saving, please wait");
      return;
    }
    const contentfullFieldsCount = Object.values(activeCardContent).filter(field => Array.isArray(field) && field.some(item => item.content && item.content.trim() !== "")).length;
    if (contentfullFieldsCount === 0) {
      notify("保存する内容がありません。");
      console.log("No content to save");
      return;
    }

    let identicalCardExists = false;
    storedCards?.forEach(card => {
      if (getCardContentDifferences(card.content, activeCardContent).existing.length === 0 && card.cardKey !== cardKey && card.cardKey !== ownerKey) {
//        setDuplicateMessageData({
//          message: "同一のデータを持つ名刺が存在します。同一の名刺は保管できません。",
//          existingCard: card,
//          options: "none"
//        });
        identicalCardExists = true;
        return;
      }
    });

    if (identicalCardExists) {
      notify("同一のデータを持つ名刺が存在します。同一の名刺は保管できません。", "保存できません");
      console.log("A card with identical data already exists, cannot save identical card.");
      return;
    }

    setIsSaving(true);
    setIsEditing(false);

    //#region Looking for existing cards with the same email as the active card - This is a bit WET, could be made a lot more DRY
    const duplicateEmailCard = (activeCardContent.email?.[0].content)
    ? storedCards?.find(card =>
        card.cardKey !== cardKey &&
        card.cardKey !== ownerKey &&
        card.content.email &&
        card.content.email.length > 0 &&
        card.content.email[0].content === activeCardContent.email?.[0].content
      ) || null
    : null;

    //const activeEnglishFirstName = activeCardContent?.firstName?.find?.(item => item.language === "english")?.content || "";
    //const activeEnglishLastName = activeCardContent?.lastName?.find?.(item => item.language === "english")?.content || "";
    //const activeEnglishName = (activeEnglishFirstName || "") + (activeEnglishFirstName && activeEnglishLastName ? " " : "") + (activeEnglishLastName || "");
    const activeJapaneseFirstName = activeCardContent?.firstName?.find?.(item => item.language === "japanese")?.content || "";
    const activeJapaneseLastName = activeCardContent?.lastName?.find?.(item => item.language === "japanese")?.content || "";
    const activeJapaneseName = (activeJapaneseLastName || "") + (activeJapaneseFirstName && activeJapaneseLastName ? "、 " : "") + (activeJapaneseFirstName || "");
    //const activeCombinedName = !activeEnglishName ? activeJapaneseName : !activeJapaneseName ? activeEnglishName : language === "japanese" ? `${activeJapaneseName} (${activeEnglishName})` : `${activeEnglishName} (${activeJapaneseName})`;

    const duplicateNameCard = (activeCardContent.firstName && activeCardContent.lastName)
    ? storedCards?.find(card => {
      if(card.cardKey === cardKey) return false;
      if(card.cardKey === ownerKey) return false;
      //const cardEnglishFirstName = card.content.firstName?.find(entry => entry.language === "english")?.content || "";
      //const cardEnglishLastName = card.content.lastName?.find(entry => entry.language === "english")?.content || "";
      const cardJapaneseFirstName = card.content.firstName?.find(entry => entry.language === "japanese")?.content || "";
      const cardJapaneseLastName = card.content.lastName?.find(entry => entry.language === "japanese")?.content || "";

      //const fullEnglishName = (activeEnglishFirstName && activeEnglishLastName);
      const fullJapaneseName = (activeJapaneseFirstName && activeJapaneseLastName);
      //if(!fullEnglishName && !fullJapaneseName) return false;
      if(!fullJapaneseName) return false;

      //const englishNameMatch = (fullEnglishName && activeEnglishFirstName === cardEnglishFirstName && activeEnglishLastName === cardEnglishLastName);
      const japaneseNameMatch = (fullJapaneseName && activeJapaneseFirstName === cardJapaneseFirstName && activeJapaneseLastName === cardJapaneseLastName);

      return (/*englishNameMatch ||*/ japaneseNameMatch);
    }) || null : null;

    if (duplicateEmailCard) {
      const duplicateEmail = duplicateEmailCard.content.email?.[0].content || "";
      console.log("Duplicate Card content and active card content:");
      console.log(duplicateEmailCard?.content);
      console.log(activeCardContent);
      const cardDifferences = getCardContentDifferences(duplicateEmailCard?.content, activeCardContent);
      console.log("Card differences:");
      console.log(cardDifferences);
      const differentFieldsDescription = getDifferenceSummary(cardDifferences);
      //const existingContent = cardDifferences.existing.map(entry => `${entry.content?.[0]?.content || "「なし」"}`).join(" / ");
      const existingContent = cardDifferences.existing.map(entry => `${entry.content?.find(obj => obj.language === "japanese")?.content || entry.content?.find(obj => obj.language === null)?.content || "「なし」"}`).join(" / ");
      //const activeContent = cardDifferences.active.map(entry => `${entry.content?.[0]?.content || "「なし」"}`).join(" / ");
      const activeContent = cardDifferences.active.map(entry => `${entry.content?.find(obj => obj.language === "japanese")?.content || entry.content?.find(obj => obj.language === null)?.content || "「なし」"}`).join(" / ");
      //const popupLine1 = `「${duplicateEmail}」${duplicateNameCard ? `と「${activeCombinedName}」さん` : ""}はすでに登録済みの可能性があります。${differentFieldsDescription}\n\n`;
      const popupLine1 = `「${duplicateEmail}」はすでに登録済みの可能性があります。${differentFieldsDescription}\n\n`;
      const popupLine2 = `現在: ${existingContent}\n`;
      const popupLine3 = `今回: ${activeContent}\n\n`;
      const popupQuestion = `上書きして登録しますか?`;
      const messageString = `${popupLine1}${popupLine2}${popupLine3}${popupQuestion}`;

      // The card being entered has the same email (but has other differences); ask the user if they want to replace the existing card.
      setDuplicateMessageData({
        message: messageString,
        existingCard: duplicateEmailCard,
        options: "replace"
      });
      setIsSaving(false);
      return;
    }
    else if (duplicateNameCard) {
      const cardDifferences = getCardContentDifferences(duplicateNameCard?.content, activeCardContent);
      const differentFieldsDescription = getDifferenceSummary(cardDifferences);
      const existingContent = cardDifferences.existing.map(entry => `${translateText(entry.field, language)}: ${entry.content?.[0]?.content || ""}`).join(", ");
      const activeContent = cardDifferences.active.map(entry => `${translateText(entry.field, language)}: ${entry.content?.[0]?.content || ""}`).join(", ");
      const popupLine1 = `「${/*activeCombinedName*/activeJapaneseName}」さんはすでに登録済みの可能性があります。${differentFieldsDescription}\n`;
      const popupLine2 = `現在: ${existingContent}\n`;
      const popupLine3 = `今回: ${activeContent}\n`;
      const popupQuestion = `上書きして登録しますか?`;
      const messageString = `${popupLine1}${popupLine2}${popupLine3}${popupQuestion}`;

      // If the card being entered has the same name but has other differences, ask the user if they want to replace the existing card, or keep both.
      setDuplicateMessageData({
        message: messageString,
        existingCard: duplicateNameCard,
        options: "add"
      });
      setIsSaving(false);
      return;
    }
    //#endregion

    uploadCard(null);
  }

  async function uploadCard(cardKeyToDelete: number | null) {

    if (!userKey || !userToken) return;

    const cardDataToSave = generateCardDataToSave();

    if(PREVENT_SAVING) {
      console.log(cardDataToSave);
      console.log("Not actually saving, remove this after debugging");
      setIsSaving(false);
      return;  
    }

    if (!cardDataToSave) {
      console.log("No data to save");
      setIsSaving(false);
      return;
    }

    const selectedTagKeys = Object.values(selectedTags).filter(Boolean) as number[];

    const cardSaveResponse = await cardApi.saveCard(cardDataToSave, selectedTagKeys, userKey, userToken, cardKey, isOwner, isPublic);

    if (!cardSaveResponse.success) {
      console.log("Failed to save card");
      setIsSaving(false);
      return;
    }

    if (cardKeyToDelete) {
      const cardDeleteResponse = await cardApi.deleteCard(cardKeyToDelete, userKey, userToken);
      if (!cardDeleteResponse.success) {
        console.log("Failed to delete duplicate card");
      }
      console.log("Deleted duplicate card with key:", cardKeyToDelete);
    }

    if (!cardSaveResponse.cardKey || typeof cardSaveResponse.cardKey !== "number") {
      console.log("No card key returned");
      setIsSaving(false);
      return;
    }
    const savedCardKey = cardSaveResponse.cardKey;
    //userApi.logUserEvent(userKey, token, 'saveText', savedCardKey);

    // Grab the canvases
    const frontCanvas = document.getElementById('frontCanvas') as HTMLCanvasElement;
    const backCanvas = document.getElementById('backCanvas') as HTMLCanvasElement;

    if (!frontCanvas || !backCanvas) {
      console.error("Canvas elements not found");
      setIsSaving(false);
      return;
    }

    // Convert canvases to Base64
    const frontImage = frontCanvas.toDataURL('image/png').split(',')[1]; // Base64 without the prefix
    const backImage = backCanvas.toDataURL('image/png').split(',')[1];  // Base64 without the prefix

    try {
      // Save front image
      if (frontCanvas?.getAttribute("contentful") === "true") {
        await cardApi.saveImage(savedCardKey, "front", userKey, userToken, frontImage);
        //userApi.logUserEvent(userKey, token, 'saveFront', savedCardKey);
      }

      // Save back image
      if (backCanvas?.getAttribute("contentful") === "true") {
        await cardApi.saveImage(savedCardKey, "back", userKey, userToken, backImage);
        //userApi.logUserEvent(userKey, token, 'saveBack', savedCardKey);
      }

      console.log("Images saved successfully");
    } catch (error) {
      console.error("Failed to save images:", error);
      setIsSaving(false);
    }

    dispatch(fetchPreload());
    setTimeout(() => {
      setIsSaving(false);
      // 保存完了後はカメラ画面ではなくトップ（/）へ遷移する
      navigate("/", { replace: true });
    }, 500);
  }


  const languageSelector = (
    <LanguageToggle value={language} onChange={setLanguage} />
  );

  const cardKeyElement = cardKey ? `名刺 #${cardKey}` : "新規の名刺";
  

//  const tagToggleElements = (
//    <>
//      <div style={{ display: "flex", gap: "0.5rem" }}>{
//        allTags.map((tag, index) => {
//          const tagIsSelected = activeTags.includes(tag.tagKey);
//          return (
//            <div key={index} style={{
//              backgroundColor: tagIsSelected ? "#35387b" : "#e0e0e0",
//              color: tagIsSelected ? "#fff" : "#666",
//              width: "8rem",
//              fontSize: "1rem",
//            }}
//              onClick={() => { handleTagToggle(tag.tagKey) }}
//              className="btn">{language === "japanese" ? (tag.japanese || tag.english || tag.name) : (tag.english || tag.japanese || tag.name)}</div>
//          );
//        })
//      }
//      </div>
//      <span style={{ display: isTagsChanged ? undefined : "none", color: "#800", fontSize: "0.8rem", margin: "0.5rem" }}>⚠️ 変更はまだ保存されていません。 ▼</span>
//    </>
//  );

  const tagDropdownElements = (
    <div className="c-fieldGroup">
      {Object.entries(tagGroups).map(([parentKey, group]) => (
        <div key={parentKey} className="c-fieldGroup__item">
          <label className="c-label" htmlFor={`card-tag-${parentKey}`}>
            {(language === "japanese" ? group.japanese : group.english) || group.title}
          </label>
          <select
            id={`card-tag-${parentKey}`}
            className="c-input"
            value={selectedTags[+parentKey] || ""}
            onChange={(e) =>
              handleTagChange(+parentKey, e.target.value ? +e.target.value : null)
            }
          >
            <option value="">
              {language === "japanese" ? "タグを選択してください" : "Choose"}
            </option>
            {group.options.map((option) => (
              <option key={option.tagKey} value={option.tagKey}>
                {(language === "japanese" ? option.japanese : option.english) || option.name}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );



  const unusedFields = FIELD_DISPLAY_ORDER.filter(key => {
    if (!activeCardContent) { return true; }
    const typedKey = key as keyof CardContentType;
    return !activeCardContent[typedKey];
  });
  //console.log("Unused fields:");
  //console.log(unusedFields);

  const addFieldButton = unusedFields.length === 0 ? null : (
    <div className="cardFields__add">
      <select
        id="addFieldSelect"
        aria-label="追加する項目"
        className="c-input cardFields__addSelect">
        {unusedFields.map((key, index) => {
          const typedKey = key as keyof CardContentType;
          return (
            <option key={index} value={typedKey}>
              {translateText(typedKey, language)}
            </option>
          );
        })}
      </select>
      <button type="button" onClick={handleAddField} className="btn btn-secondary">
        追加
      </button>
    </div>
  );


  const fieldElements =
    (!activeCardContent && !isEditing) ? (<span>No card data</span>) :
      (!activeCardContent) ? null :
        FIELD_DISPLAY_ORDER.map((key) => {
          const typedKey = key as keyof CardContentType;
          const currentField = activeCardContent[typedKey];
          if (!currentField) { return null; }

          const fieldLanguage = language || "japanese";
          const fieldName = translateText(key, fieldLanguage);

          const filteredField = TrimUnwantedLanguageFieldData(currentField) || [];

          // If there's only one value for a field, display it in a text input
          const currentTextInput = filteredField.length === 1 ? (<input className={`c-input ${isEditing ? "" : "readonly"}`} aria-label={fieldName} type="text" readOnly={!isEditing} onChange={(event) => { HandleChangeField(event, typedKey) }} value={filteredField[0].content} />) : null;

          // If there's more than one value for a field, display it in a select dropdown, plus a text input that covers the select dropdown, so the select dropdown content can be edited
          const selectClassname = `c-input ${isEditing ? "" : "readonly"} datum-select`;
          const currentSelectIndex = selectedOptions[typedKey]?.find(so => (so.language === language || !so.language))?.optionIndex || 0;
          const currentSelectInput = filteredField.length > 1 ? (<select onChange={(event) => HandleSelectChange(event, typedKey)} value={currentSelectIndex} className={selectClassname} aria-label={fieldName} name={filteredField[0].language || "none"}>
            {filteredField.map((item: CardDatumType, index: number) => {
              return (<option key={index} value={index}>{item.content}</option>)
            })}
          </select>) : null;

          const overlayContent = filteredField[currentSelectIndex]?.content || "";
          const overlayTextInput = <input className="c-input datum-input-overlay" aria-label={fieldName} type="text" value={overlayContent} onChange={(event) => { HandleChangeSelectContent(event, typedKey, currentSelectIndex) }} />
          const currentCustomSelect = <div className="custom-select-wrapper">
            {currentSelectInput}
            {isEditing ? overlayTextInput : null}
          </div>


          const currentElement = filteredField.length === 0 ? null : filteredField.length === 1 ? currentTextInput : currentCustomSelect;


          // When just displaying data (not editing), backup content from another language is displayed if available (and the requested language content isn't)
          // However, when editing, always show the content for the selected language, even if empty. That will allow the user to add content in that language.
          let flag = "";
          if (filteredField[0].language === "english") { flag = "🇬🇧"; }
          if (filteredField[0].language === "japanese") { flag = "🇯🇵"; }
          if (isEditing && filteredField[0].language) {
            if (language === "english") { flag = "🇬🇧"; }
            if (language === "japanese") { flag = "🇯🇵"; }
          }
          const flagSpan = <span className="flag-span">{flag}</span>

          let mapLink = null;
          if (filteredField.length === 1 && (typedKey === "address" || typedKey === "headAddress")) {
            mapLink = <a href={`https://www.google.com/maps/search/?api=1&query=${filteredField[0].content}`} target="_blank" rel="noreferrer"><img src={iconAddress} /></a>;
          }
          if (filteredField.length > 1 && filteredField[currentSelectIndex]?.content && (typedKey === "address" || typedKey === "headAddress")) {
            mapLink = <a href={`https://www.google.com/maps/search/?api=1&query=${filteredField[currentSelectIndex]?.content}`} target="_blank" rel="noreferrer"><img src={iconAddress} /></a>;
          }

          let urlLink = null;
          if (filteredField.length === 1 && typedKey === "url") {
            urlLink = <a href={filteredField[0].content} target="_blank" rel="noreferrer"><img src={iconLink} /></a>;
          }
          if (filteredField.length > 1 && filteredField[currentSelectIndex]?.content && typedKey === "url") {
            urlLink = <a href={filteredField[currentSelectIndex]?.content} target="_blank" rel="noreferrer"><img src={iconLink} /></a>;
          }

          let emailLink = null;
          if (filteredField.length === 1 && typedKey === "email") {
            emailLink = <a href={`mailto:${filteredField[0].content}`}><img src={iconEmail} /></a>;
          }
          if (filteredField.length > 1 && filteredField[currentSelectIndex]?.content && typedKey === "email") {
            emailLink = <a href={`mailto:${filteredField[currentSelectIndex]?.content}`}><img src={iconEmail} /></a>;
          }

          let phoneLink = null;
          if (filteredField.length === 1 && (typedKey === "phone" || typedKey === "mobile" || typedKey === "headPhone")) {
            phoneLink = <a href={`tel:${filteredField[0].content}`}><img src={iconPhone} /></a>;
          }
          if (filteredField.length > 1 && filteredField[currentSelectIndex]?.content && (typedKey === "phone" || typedKey === "mobile" || typedKey === "headPhone")) {
            phoneLink = <a href={`tel:${filteredField[currentSelectIndex]?.content}`}><img src={iconPhone} /></a>;
          }

          const deleteButton = <button type="button" onClick={() => { handleDeleteField(typedKey) }} className="delete-icon" aria-label={`${fieldName}を削除`} />;

          
          return (
            <div key={key} className={`cardField${isEditing ? " is-editing" : ""}`}>
              <div className="cardField__label">{fieldName}</div>
              <div className="cardField__value">
                {isEditing ? currentElement : filteredField[0].content}
              </div>
              <div className="cardField__actions">
                {mapLink}{urlLink}{emailLink}{phoneLink}
                {isEditing && deleteButton}
              </div>
            </div>
          );
        });


  const publicCardToggle = (
    <div className="flex items-center p-2" style={{display: isAdmin ? undefined : "none"}}>
      <input
        type="checkbox"
        id="publicCardToggle"
        className="w-4 h-4 mr-2"
        checked={isPublic}
        onChange={(e) => {
          setIsPublic(e.target.checked);
        }}
      />
      <label htmlFor="publicCardToggle" className="text-sm">
        {language === "japanese" ? "公開の名刺" : "Public Card"}
      </label>
    </div>
  );

  function HandleSelectChange(event: React.ChangeEvent<HTMLSelectElement>, field: keyof CardContentType) {
    setSelectedOptions((prev) => {
      const eventSelect = event.target;
      const selectLanguage = eventSelect.name === "none" ? undefined : eventSelect.name as LanguageEnum;
      //const selectOptionLanguage = activeCardContent?.[field]?.[0].language; // undefined for "no language" content
      const selectOptionLanguage = selectLanguage;
      const newOptionIndex = parseInt(event.target.value, 10);
      const newSelectOptionObject = selectOptionLanguage ? { language: selectOptionLanguage, optionIndex: newOptionIndex } : { optionIndex: newOptionIndex };
      const newSelectOptionValue = prev[field] ? [...prev[field].filter(prevField => prevField.language !== selectOptionLanguage), newSelectOptionObject] : [newSelectOptionObject];
      console.log("Change select: " + field + " to " + newOptionIndex + " in language " + selectOptionLanguage);
      const newSelectOptionValues = { ...prev, [field]: newSelectOptionValue };
      console.log(newSelectOptionValues);
      return newSelectOptionValues;
    });
  }

  function HandleChangeSelectContent(event: React.ChangeEvent<HTMLInputElement>, field: keyof CardContentType, currentSelectIndex: number) {
    setActiveCardContent((prevData) => {
      if (!prevData) { prevData = { ...emptyCardContent }; }
      const newData = { ...prevData };
      const currentField = TrimUnwantedLanguageFieldData(newData[field]) || [];
      const currentFieldObject = currentField[currentSelectIndex];
      //console.log("Change select content: " + field + " from: " + currentField[currentSelectIndex].content + " to " + event.target.value + " in " + language + " at index " + currentSelectIndex);
      currentFieldObject.content = event.target.value;
      return newData;
    });
  }

  function TrimUnwantedLanguageFieldData(inputField: CardDatumType[] | null, desiredLanguage: LanguageEnum = language, forceDesiredLanguage: boolean = isEditing): CardDatumType[] | null {
    if (!inputField) { return null; }

    const noLanguageValues = inputField.filter?.((item: CardDatumType) => !item.language);
    const englishValues = inputField.filter?.((item: CardDatumType) => item.language === "english");
    const japaneseValues = inputField.filter?.((item: CardDatumType) => item.language === "japanese");

    // If there is "no language" content, there is no language-specific content
    // When editing or saving, the content must be in the selected language, so forceDesiredLanguage is true
    // If editing, the content must be either in the selected language or "no language" content
    // If not editing nor saving (i.e. just displaying), content from the other language is displayed if the requested language content isn't available, thus forceDesiredLanguage is false
    // Example: If editing, and language is japanese, use [] instead of englishValues
    //          If not editing, and language is japanese, and there is japanese content, use [] instead of englishValues
    const filteredField = [...noLanguageValues, ...((desiredLanguage === "japanese" && (forceDesiredLanguage || japaneseValues.length > 0)) ? [] : englishValues), ...((desiredLanguage === "english" && (forceDesiredLanguage || englishValues.length > 0)) ? [] : japaneseValues)];

    // If there is content for a field, but none for the selected language, add an empty string for the selected language.
    // This means when the user starts to edit the field, they can add content in the selected language.
    if (filteredField.length === 0) { filteredField.push({ content: "", language: desiredLanguage }); }

    return filteredField;
  }

  function generateCardDataToSave(): CardContentType | null {
    const cardDataToSave: Partial<CardContentType> = {};

    for (const field in activeCardContent) {
      const typedField = field as keyof CardContentType;
      const fieldArray = activeCardContent[typedField];
      const fieldSelections = selectedOptions[typedField];

      if (fieldArray === null) {
        // If there's no data, set the field to null
        cardDataToSave[typedField] = null;
        continue;
      }

      const selectedItems: CardDatumType[] = [];
      const languagesToProcess: ("english" | "japanese" | null)[] = ["english", "japanese", null];

      for (const languageToProcess of languagesToProcess) {

        // If this field doesn't have a language, but we're looking for a value in a specific language, skip the search
        if (languageToProcess === null && !noLanguageFields[typedField]) { continue; }

        // If this field has a language, but we're looking for a value with no language, skip the search
        if (languageToProcess !== null && noLanguageFields[typedField]) { continue; }

        // Check if there is a selection for this language
        // If we're looking for data with no language, check that selection.language is undefined (not null)
        const selectionForLanguage = fieldSelections?.find((selection) => languageToProcess === null ? selection.language === undefined : selection.language === languageToProcess);

        // Default to index 0 if no selection exists
        const optionIndex = selectionForLanguage ? selectionForLanguage.optionIndex : 0;

        // Filter the field array using TrimUnwantedLanguageFieldData
        // "true" forces the desired language to be used, even if it's not in the field array
        const filteredArray = TrimUnwantedLanguageFieldData(fieldArray, languageToProcess, true) || [];
        const selectedLanguageObject = filteredArray.length > optionIndex ? filteredArray[optionIndex] : null;

        if (selectedLanguageObject) {
          selectedItems.push(selectedLanguageObject);
        }
      }

      cardDataToSave[typedField] = selectedItems.length > 0 ? selectedItems : null;
    }

    return cardDataToSave as CardContentType;
  }

  // const headerName = language === "english" ? englishName : language === "japanese" ? japaneseName : (<div style={{ display: "flex", flexDirection: "column", lineHeight: "1.25rem" }}><span>{japaneseName}</span><span>{englishName}</span></div>);
  const header = (
    <div className="flex justify-start">
      {/* <span className="header-name">{headerName}</span> */}
      <button type="button" className="btn btn-secondary w-full md:w-fit" onClick={() => setIsCaptureMode(true)}>
        {isUploadOnly ? "アップロード" : "名刺をスキャンへ"}
      </button>
    </div>
  );

  const footer = (
    <div className="cardFooter">
      <button
        type="button"
        onClick={() => setIsEditing(!isEditing)}
        className={`btn ${isEditing ? "btn-primary" : "btn-secondary"}`}
      >
        {isEditing ? "確認" /*"保存せず終了"*/ : "編集"}
      </button>
      {(!isEditing && activeCardContent) && (
        <button
          type="button"
          onClick={handleSaveCard}
          className="btn btn-primary"
        >
          {isSaving ? "保存中..." : "保存"}
        </button>
      )}
      {PREVENT_SAVING ? (
        <div className="btn btn-primary" onClick={handleSaveCard}>
          デバッグ保存
        </div>
      ) : null}
    </div>
  );

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
  const frontImageSrc = (storedCard && storedCard.hasFrontImage) ? `${baseUrl}/images/thumbnails/card${storedCard.cardKey}front.jpg?reload=${Date.now()}` : undefined;
  const backImageSrc = (storedCard && storedCard.hasBackImage) ? `${baseUrl}/images/thumbnails/card${storedCard.cardKey}back.jpg?reload=${Date.now()}` : undefined;

  // Set image to non-existant if there's no image URL available, or the image failed to load
  const frontImageExists = !(!frontImageSrc || imageLoadError.front);
  const backImageExists = !(!backImageSrc || imageLoadError.back);

  const frontCanvasContentful = unsavedCanvas.front === true;
  const backCanvasContentful = unsavedCanvas.back === true;

  const unsavedWarning = <span className="unsavedWarning">(保存されません)</span>

  const cardFrontCanvas = (
    <div className="block aspect-[5/3] w-full" style={{ display: frontCanvasContentful ? undefined : "none" }}>
      <canvas id="frontCanvas" ref={canvases.viewer.front} className={`output-canvas w-full h-full ${unsavedCanvas.front ? "card-canvas-container-unsaved" : ""}`}></canvas>
    </div>
  );

  const cardBackCanvas = (
    <div className="block aspect-[5/3] w-full" style={{ display: backCanvasContentful ? undefined : "none" }}>
      <canvas id="backCanvas" ref={canvases.viewer.back} className={`output-canvas w-full h-full ${unsavedCanvas.back ? "card-canvas-container-unsaved" : ""}`}></canvas>
    </div>
  );

  const showFrontImage = frontImageExists && !frontCanvasContentful;
  const showBackImage = backImageExists && !backCanvasContentful;
  const showMockCard = (!frontImageExists && !backImageExists && !frontCanvasContentful && !backCanvasContentful);

  const cardsDisplayElements = (
    <div className="flex flex-col sm:flex-row gap-4 w-full max-w-screen-md mx-auto">

      <div className="w-full sm:w-1/2">
        <div className="text-lg">前面{unsavedCanvas.front ? unsavedWarning : null}</div>
        {cardFrontCanvas}
        {/* If there's no image for the front, or it failed to load, show a fallback svg */}
        {showMockCard ?
          <MockCard content={activeCardContent} language={language}/> :
          showFrontImage ?
          <div className="block aspect-[5/3] w-full">
            <img src={frontImageSrc} className="w-full h-full object-cover drop-shadow-lg" onError={() => { setImageLoadError(prev => { return { ...prev, front: true } }) }} /></div> :
            !frontCanvasContentful ?
              <CardErrorSvg text="前面なし"  /> : null
        }
      </div>
      <div className="w-full sm:w-1/2">
        <div className="text-lg">裏面{unsavedCanvas.back ? unsavedWarning : null}</div>
        {cardBackCanvas}
        {showBackImage ?
        <div className="block aspect-[5/3] w-full">
          <img src={backImageSrc} className="w-full h-full object-cover drop-shadow-lg" onError={() => { setImageLoadError(prev => { return { ...prev, back: true } }) }} /></div> :
          !backCanvasContentful ?
            <CardErrorSvg text="裏面なし"  /> : null
        }
      </div>

 
    </div>
  );

  const duplicateCardModal = () => {
    const canOverwrite = duplicateMessageData?.options === "replace" || duplicateMessageData?.options === "add";
    const canAdd = duplicateMessageData?.options === "add";
    // 削除の確認などと同じモーダルの形（c-modal）。上書きは元の名刺の内容が消えるので危険の色
    return createPortal(
      <div style={{ display: duplicateMessageData ? undefined : "none" }} className="c-modalOverlay">
        <div className="c-modal c-modal--inline" role="dialog" aria-modal="true" aria-labelledby="duplicate-modal-title">
          <div className="c-modal__inner">
            <h2 id="duplicate-modal-title" className="c-modal__title">重複した名刺が存在します</h2>
            <p className="c-modal__message whitespace-pre-line">{duplicateMessageData?.message}</p>
            <div className="c-modal__actions c-modal__actions--wrap">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setDuplicateMessageData(null);
                }}
              >
                キャンセル
              </button>
              {canAdd && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setDuplicateMessageData(null);
                    uploadCard(null);
                  }}
                >
                  新規で登録
                </button>
              )}
              {canOverwrite && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    setDuplicateMessageData(null);
                    uploadCard(duplicateMessageData?.existingCard?.cardKey || null);
                  }}
                >
                  上書きして登録
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    , document.getElementById("modal-root") as HTMLElement);
  };

  return (
    // 名刺トップと同じ「見出し帯つきの箱」を縦に3つ並べる（名刺の画像 / タグ / 名刺の内容）
    <div className="cardBoxes" style={{ display: isCaptureMode ? "none" : undefined }}>
      {duplicateMessageData && duplicateCardModal()}
      <section className="drop-shadow-md">
        <div className="bg-primary-gray rounded-t-md flex justify-between items-center">
          <h2 className="c-box__title">名刺の画像</h2>
          <span className="c-box__meta">{cardKeyElement}</span>
        </div>
        <div className="bg-white rounded-b-md c-box__body cardBox__body">
          {header}
          {cardsDisplayElements}
        </div>
      </section>
      <section className="drop-shadow-md">
        <div className="bg-primary-gray rounded-t-md">
          <h2 className="c-box__title">タグ</h2>
        </div>
        <div className="bg-white rounded-b-md c-box__body">
          {tagDropdownElements}
        </div>
      </section>
      <section className="drop-shadow-md">
        <div className="bg-primary-gray rounded-t-md">
          <h2 className="c-box__title">名刺の内容</h2>
        </div>
        <div className="bg-white rounded-b-md c-box__body cardBox__body">
          {languageSelector}
          <div>
            {fieldElements}
            {isEditing && addFieldButton}
          </div>
          {publicCardToggle}
          {footer}
        </div>
      </section>
    </div>
  )
}

export default CardViewer;

const mergeCardContents = (
  cardContent1: CardContentType | null,
  cardContent2: CardContentType | null
): CardContentType | null => {
  if (!cardContent1) return (cardContent2 || null);
  if (!cardContent2) return cardContent1;

  const mergedContent: CardContentType = { ...emptyCardContent };

  for (const key of Object.keys(mergedContent) as (keyof CardContentType)[]) {
    const array1 = cardContent1[key];
    const array2 = cardContent2[key];

    if (!array1 && !array2) {
      mergedContent[key] = null;
    } else if (!array1) {
      mergedContent[key] = array2;
    } else if (!array2) {
      mergedContent[key] = array1;
    } else {
      // Merge arrays and remove duplicates
      const mergedArray = [...array1, ...array2].filter(
        (value, index, self) =>
          index ===
          self.findIndex(
            (v) =>
              v.content === value.content &&
              v.language === value.language &&
              v.face === value.face
          )
      );
      mergedContent[key] = mergedArray;
    }
  }

  return mergedContent;
};


function removeFaceContent(
  cardContent: CardContentType | null,
  face: "front" | "back"
): CardContentType | null {
  if (!cardContent) return null;

  const newCardContent: CardContentType = { ...cardContent };

  for (const key in newCardContent) {
    const dataArray = newCardContent[key as keyof CardContentType];
    if (Array.isArray(dataArray)) {
      newCardContent[key as keyof CardContentType] = dataArray.filter(
        datum => datum.face !== face
      );
    }
  }

  return newCardContent;
}


function getCardContentDifferences(
  existingContent: CardContentType,
  activeContent: CardContentType
): DifferenceResultType {
  const fields: (keyof CardContentType)[] = [
    'lastName',
    'firstName',
    'lastNamePhonetic',
    'firstNamePhonetic',
    'companyName',
    'branch',
    'title',
    'email',
    'url',
    'headPhone',
    'headFax',
    'headAddress',
    'phone',
    'mobile',
    'fax',
    'address',
  ];

  const existing: DifferenceEntry[] = [];
  const active: DifferenceEntry[] = [];

  for (const field of fields) {
    const existingValue = existingContent[field];
    const activeValue = activeContent[field];

    const japaneseContent = existingValue?.find((item) => item.language === "japanese")?.content || null;
    const noLanguageContent = existingValue?.find((item) => item.language === null)?.content || null;

    const isExistingValueEmpty = (existingValue == null || existingValue?.length === 0 || (!japaneseContent && !noLanguageContent));
    const isActiveValueEmpty = (activeValue == null || activeValue?.length === 0 || (!japaneseContent && !noLanguageContent));

    if (isExistingValueEmpty && isActiveValueEmpty) {
      continue;
    }

    // If one is empty and the other isn't, it's definitely a difference
    if (isExistingValueEmpty !== isActiveValueEmpty) {
        existing.push({ field, content: existingValue });
        active.push({ field, content: activeValue });
        continue;
    }

//    if (Array.isArray(existingValue) && Array.isArray(activeValue)) {
//      const matchedExistingIndexes = new Set<number>();
//      const matchedActiveIndexes = new Set<number>();
//
//      for (let i = 0; i < existingValue.length; i++) {
//        const exItem = existingValue[i];
//        for (let j = 0; j < activeValue.length; j++) {
//          const acItem = activeValue[j];
//          const sameContent = exItem.content === acItem.content;
//          const languagesMatch =
//            exItem.language === acItem.language ||
//            exItem.language == null ||
//            acItem.language == null;
//
//          if (sameContent && languagesMatch) {
//            matchedExistingIndexes.add(i);
//            matchedActiveIndexes.add(j);
//            break;
//          }
//        }
//      }
//
//      const differingExisting = existingValue.filter((_, idx) => !matchedExistingIndexes.has(idx));
//      const differingActive = activeValue.filter((_, idx) => !matchedActiveIndexes.has(idx));
//
//      if (differingExisting.length > 0 || differingActive.length > 0) {
//        existing.push({ field, content: differingExisting });
//        active.push({ field, content: differingActive });
//      }
      if (Array.isArray(existingValue) && Array.isArray(activeValue)) {
        const existingObject = existingValue.find((item) => item.language === "japanese") || existingValue.find((item) => !item.language) || null;
        const isExistingContentJapanese = existingObject?.language === "japanese" || existingObject?.language === null;
        const existingText = existingObject?.content.trim() || "";
        const activeObject = activeValue.find((item) => item.language === "japanese") || activeValue.find((item) => !item.language) || null;
        const isActiveContentJapanese = activeObject?.language === "japanese" || activeObject?.language === null;
        const activeText = activeObject?.content.trim() || "";

        // It's a match if the content is the same, or all content is non-Japanese
        const isMatch = ((existingText === activeText) || (!isExistingContentJapanese && !isActiveContentJapanese));

        if (!isMatch) {
          existing.push({ field, content: existingValue });
          active.push({ field, content: activeValue });
        }
    } else {
      if (!existingValue && !activeValue) continue;
      if (JSON.stringify(existingValue) !== JSON.stringify(activeValue)) {
        existing.push({ field, content: existingValue });
        active.push({ field, content: activeValue });
      }
    }
  }

  const returnedValues = { existing, active };
  //console.log("Differences found:");
  //console.log(returnedValues);
  return { existing: existing.slice(0,2), active: active.slice(0,2) };
};

function getDifferenceSummary(diff: DifferenceResultType): string | null {
  const fieldNames = diff.existing.map(entry => entry.field);

  if (fieldNames.length === 0) {
    return "違いはありません。";
  }

  const limitedFields = fieldNames.slice(0, 2);

  let fieldList = '';
  if (limitedFields.length === 1) {
    fieldList = `「${translateText(limitedFields[0], "japanese")}」`;
  } else /*if (limitedFields.length === 2)*/ {
    fieldList = `「${translateText(limitedFields[0],"japanese")}」と「${translateText(limitedFields[1], "japanese")}」`;
//  } else {
//    fieldList = `${limitedFields[0]}, ${limitedFields[1]}, と ${limitedFields[2]}`;
  }

  return `${fieldList} に違いがあります。`;
};
